// ABOUTME: React Query mutation hooks for creating, updating, and deleting quantity configurations.
// ABOUTME: Builds the 3-section full-replace request sending overridden fields (plus the Full Loads UoM/options backfill required when isEnabled is overridden to true), and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  FullLoadsSectionRequest,
  LoadBalanceSectionRequest,
  QuantityApiResponse,
  QuantityFormValues,
  QuantityOverrides,
  QuantitySectionRequest,
  QuantityUomIds,
  UpsertQuantityRequest,
} from '../types/quantity'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

/**
 * Builds the upsert request from form values + per-field override flags. Only fields flagged as
 * overridden are sent and sections with no overridden fields are omitted (full-replace semantics), with
 * one exception: when Full Loads isEnabled is overridden to true, quantityUomId and options are also sent
 * (backfilled from the inherited form values) to satisfy the backend section-enablement invariant.
 * Exported for unit testing.
 */
export const toApiRequest = (
  values: QuantityFormValues,
  overrides: QuantityOverrides,
  uomIds: QuantityUomIds
): UpsertQuantityRequest => {
  const quantity = buildQuantitySection(values, overrides, uomIds)
  const fullLoads = buildFullLoadsSection(values, overrides, uomIds)
  const loadBalance = buildLoadBalanceSection(values, overrides)

  const request: UpsertQuantityRequest = {}
  if (Object.keys(quantity).length > 0) request.quantity = quantity
  if (Object.keys(fullLoads).length > 0) request.fullLoads = fullLoads
  if (Object.keys(loadBalance).length > 0) request.loadBalance = loadBalance
  return request
}

const buildQuantitySection = (
  values: QuantityFormValues,
  overrides: QuantityOverrides,
  uomIds: QuantityUomIds
): QuantitySectionRequest => {
  const quantity: QuantitySectionRequest = {}
  if (overrides.uom) quantity.uomId = { isOverridden: true, value: uomIds.uomId ?? null }
  if (overrides.default) quantity.default = { isOverridden: true, value: values.default }
  if (overrides.min) quantity.min = { isOverridden: true, value: values.min }
  if (overrides.max) quantity.max = { isOverridden: true, value: values.max }
  if (overrides.maxOrderConfirmable)
    quantity.maxOrderConfirmable = { isOverridden: true, value: values.maxOrderConfirmable }
  if (overrides.step) quantity.step = { isOverridden: true, value: values.step }
  if (overrides.shortLoadThreshold)
    quantity.shortLoadThreshold = { isOverridden: true, value: values.shortLoadThreshold }
  if (overrides.numberOfDecimals)
    quantity.numberOfDecimals = { isOverridden: true, value: values.numberOfDecimals }
  if (overrides.roundToChangeIncrement)
    quantity.roundToChangeIncrement = { isOverridden: true, value: values.roundToChangeIncrement }
  return quantity
}

const buildFullLoadsSection = (
  values: QuantityFormValues,
  overrides: QuantityOverrides,
  uomIds: QuantityUomIds
): FullLoadsSectionRequest => {
  const fullLoads: FullLoadsSectionRequest = {}
  if (overrides.fullLoadsEnabled)
    fullLoads.isEnabled = { isOverridden: true, value: values.fullLoadsEnabled }
  if (overrides.fullLoadsUom)
    fullLoads.quantityUomId = { isOverridden: true, value: uomIds.fullLoadsUomId ?? null }
  if (overrides.fullLoadsOptions)
    fullLoads.options = { isOverridden: true, value: values.fullLoadOptions }

  // The backend rejects the section only when isEnabled is sent as an override with value true while
  // quantityUomId or options are not also overridden in the same request
  // (QuantityConfigurationValidator.ValidateSectionEnablement). Mirror that exact condition: when we
  // send isEnabled=true, also send quantityUomId and options as overrides, backfilling the inherited
  // values carried in the form. Inherited/disabled cases are left untouched so inheritance is preserved.
  if (fullLoads.isEnabled?.isOverridden && fullLoads.isEnabled.value === true) {
    if (!fullLoads.quantityUomId)
      fullLoads.quantityUomId = { isOverridden: true, value: uomIds.fullLoadsUomId ?? null }
    if (!fullLoads.options)
      fullLoads.options = { isOverridden: true, value: values.fullLoadOptions }
  }
  return fullLoads
}

const buildLoadBalanceSection = (
  values: QuantityFormValues,
  overrides: QuantityOverrides
): LoadBalanceSectionRequest => {
  const loadBalance: LoadBalanceSectionRequest = {}
  if (overrides.loadBalanceEnabled)
    loadBalance.isEnabled = { isOverridden: true, value: values.loadBalanceEnabled }
  if (overrides.minEligibleQuantity)
    loadBalance.minEligibleQuantity = { isOverridden: true, value: values.minEligibleQuantity }
  if (overrides.maxEligibleQuantity)
    loadBalance.maxEligibleQuantity = { isOverridden: true, value: values.maxEligibleQuantity }
  return loadBalance
}

interface MutationArgs {
  scopeId: string
  values: QuantityFormValues
  overrides: QuantityOverrides
  uomIds: QuantityUomIds
}

export const useCreateQuantity = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides, uomIds }: MutationArgs) => {
      const res = await api.post<QuantityApiResponse>(
        `/tuc-configs/quantity/${scopeId}`,
        toApiRequest(values, overrides, uomIds)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quantityConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'quantity', countryId, businessLine] })
    },
  })
}

export const useCreateQuantityWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values, overrides, uomIds }: Omit<MutationArgs, 'scopeId'> & { scope: PendingScopeRequest }) => {
      const res = await api.post<QuantityApiResponse>(
        '/tuc-configs/quantity',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values, overrides, uomIds))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quantityConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'quantity', countryId, businessLine] })
    },
  })
}

export const useUpdateQuantity = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides, uomIds }: MutationArgs) => {
      const res = await api.put<QuantityApiResponse>(
        `/tuc-configs/quantity/${scopeId}`,
        toApiRequest(values, overrides, uomIds)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quantityConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'quantity', countryId, businessLine] })
    },
  })
}

export const useDeleteQuantity = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/quantity/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quantityConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'quantity', countryId, businessLine] })
    },
  })
}
